<template>
  <div class="testimonials-page">
    <div class="hero-section">
      <div class="container">
        <div class="row centered">
          <div class="head">
            <h1>{{ page.title }}</h1>
            <p>{{ page.content }}</p>
          </div>
          <div class="info">
            <div class="block sm-2 col-2">
              <div class="ttl">
                2M
              </div>
              <div class="desc">
                Students Worldwide
              </div>
            </div>
            <div class="block sm-2 col-2">
              <div class="ttl">
                {{ stats.rounded.completedCourses }}
              </div>
              <div class="desc">
                Courses Completed
              </div>
            </div>
            <div class="block sm-2 col-2">
              <div class="ttl">
                {{ stats.rounded.certificatesEarned }}
              </div>
              <div class="desc">
                Certificates Earned
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Lazy never>
      <section class="container testimonials">
        <h2>Featured Success Stories</h2>
        <div class="cards">
          <TestimonialsCard
            v-for="(data, i) in testimonials" :key="i" :data="data"
            class="tracking-section"
            :index="i" :data-event="`story_${i + 1}.scroll`"
          />
        </div>
        <div class="story tracking-section" data-event="share_story.scroll">
          <div class="row">
            <div class="col col-6 offset-3 sm-offset-0">
              <p class="h3">
                {{ $c.text('pages.success-stories.sectionYourStory.title') }}
              </p>
              <p class="subtitle">
                {{ $c.text('pages.success-stories.sectionYourStory.description') }}
              </p>
              <nuxt-link to="/contact-us/" class="btn-primary btn-lg" data-event="share_your_story.click">
                Share Your Story
              </nuxt-link>
            </div>
          </div>
        </div>
        <div class="graduates-work tracking-section" data-event="graduateswork.scroll">
          <h3 class="h2">
            Our Graduates Work at Top Companies Worldwide
          </h3>
          <div class="logos">
            <span class="logo">
              <LogoVisa />
            </span>
            <span class="logo">
              <LogoWalmart />
            </span>
            <span class="logo">
              <LogoTesla />
            </span>
            <span class="logo">
              <LogoCitibank />
            </span>
            <span class="logo">
              <LogoOracle />
            </span>
            <span class="logo">
              <LogoPaypal />
            </span>
            <span class="logo">
              <LogoAmazon />
            </span>
          </div>
        </div>
      </section>
    </Lazy>
    <div class="our-students tracking-section" data-event="reviews.scroll">
      <section class="container">
        <h2>
          {{ $c.text('pages.success-stories.sectionOurStudents.title') }}
        </h2>
        <div class="reviews">
          <article
            v-for="(review, i) in reviews" :key="i" class="review"
          >
            <div class="holder">
              <div class="body">
                <Rating :scale="review.rating" />
                <div class="text">
                  {{ review.text }}
                </div>
              </div>
              <div class="info">
                <img loading="lazy" :src="`${$config.imagesUrl}/reviews/${review.image}`" :alt="review.name">
                <div>
                  <div class="name">
                    {{ review.name }} <Check />
                  </div>
                  {{ review.country }}
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
    <PreFooter
      title="Ready to Start Your Data Science Career?"
      description="Learn from the best, gain the top skills employers require, and secure a job in data science."
      button-title="Start Now"
      data-event="start_your_career.click"
      button-link="/signup/"
    />
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import TestimonialsCard from '../../components/TestimonialsCard';
import Rating from '../../components/Rating';
import PreFooter from '../../components/PreFooter';
import LogoVisa from '../../assets/svg/companies/logo-visa.svg?vue-component';
import LogoWalmart from '../../assets/svg/companies/logo-walmart.svg?vue-component';
import LogoTesla from '../../assets/svg/companies/logo-tesla.svg?vue-component';
import LogoCitibank from '../../assets/svg/companies/logo-citibank.svg?vue-component';
import LogoOracle from '../../assets/svg/companies/logo-oracle.svg?vue-component';
import LogoPaypal from '../../assets/svg/companies/logo-paypal.svg?vue-component';
import LogoAmazon from '../../assets/svg/companies/logo-amazon.svg?vue-component';
import Check from '../../assets/svg/icon-check-circle.svg?vue-component';

export default {
  components: {
    Lazy, TestimonialsCard, PreFooter, Rating, LogoVisa, LogoWalmart, LogoTesla, LogoCitibank, LogoOracle, LogoPaypal, LogoAmazon, Check,
  },
  mixins: [global],
  async asyncData({
    $axios, route, $config, error,
  }) {
    const [testimonials, page, stats] = await Promise.all([
      $axios.$get('/testimonials'),
      $axios.$get(`/pages/${route.fullPath}`).catch(() => false),
      $axios.$get('/stats/courses?reduced=1').catch(() => ({})),
    ]);

    if (!page || $config.platform !== 'ds') {
      return error({ title: 'Not found', statusCode: 404, message: 'Page not found' });
    }

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      testimonials,
      page,
      stats,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    reviews() {
      return [
        {
          text: "I've only just started but I can tell the 365 team put so much effort into structuring the course neatly, writing the scripts clearly and creating simple to understand analogies for certain...",
          name: 'Cherry Lim',
          country: 'Singapore',
          rating: 5,
          image: 'Cherry Lim.jpg',
        },
        {
          text: 'I was so excited when I stumbled on 365 Data Science. I had been looking for a platform where i could learn all about data from scratch and learn in a fun way. i was happy to find they present modul...',
          name: 'Chinonso Edeh',
          country: 'Nigeria',
          rating: 5,
          image: 'Chinonso Edeh.jpg',
        },
        {
          text: 'Awesome courses. Simple and quite effective to learn data science. I recommend it to anyone who would like to learn data science from scratch.',
          name: 'Soumyabrata Roy',
          country: 'India',
          rating: 5,
          image: 'soumyabrata roy.jpg',
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.testimonials-page {
  .hero-section {
    position: relative;
    min-height: 760px;
    background-image: url('../../assets/images/hero-testimonials.jpg');
    image-rendering: -webkit-optimize-contrast;
    color: var(--true-white);
    display: flex;
    justify-content: center;
    text-align: center;
    align-items: center;
    background-size: cover;
    background-position: 50%;

    &:after,
    &:before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
    }

    &:before {
      z-index: 0;
      background: rgba(0, 12, 31, 0.74);
    }

    &:after {
      z-index: 1;
      background: radial-gradient(63.72% 63.72% at 50% 50%, rgba(0, 12, 31, 0.5) 0%, rgba(0, 12, 31, 0) 100%);
    }

    .container {
      position: relative;
      z-index: 2;
    }

    .head {
      max-width: 100%;
      width: 720px;

      p {
        font-size: 21px;
        color: var(--main-dark-shade-4);
        padding: 0 83px;
        margin-top: var(--spacing-16);
      }
    }

    .info {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 100px;
      margin-top: 111px;

      .block {
        .ttl {
          font-size: 46px;
          line-height: 46px;
        }

        .desc {
          color: var(--main-dark-shade-4);
        }
      }
    }
  }

  .testimonials {
    padding-top: var(--spacing-120);

    h2 {
      text-align: center;
    }

    .cards {
      margin-top: var(--spacing-60);
    }
  }

  .story {
    margin-top: var(--spacing-60);
    text-align: center;

    .btn-primary {
      width: 280px;
    }
  }

  .graduates-work {
    margin-top: var(--spacing-120);
    border-radius: 14px;
    background-color: var(--main-light-shade-1);
    padding: var(--spacing-80);

    .h2 {
      text-align: center;
      padding: 0 var(--spacing-160);
    }

    .logos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin-left: -16px;
      margin-right: -16px;

      .logo {
        padding: var(--spacing-32) 50px;
        background-color: var(--main-light);
        box-shadow: 0 16px 30px rgba(0, 12, 31, 0.08);
        border-radius: 7px;
        margin: var(--spacing-16);
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 128px;

          path[fill="#fff"] {
            fill: var(--main-dark-shade-3);
          }
        }
      }
    }
  }

  .our-students {
    background-color: var(--main-dark);
    padding: var(--spacing-120) 0;
    margin-top: var(--spacing-120);
    color: var(--main-light);

    h2 {
      text-align: center;
      padding: 0 300px;
    }

    .reviews {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .review {
      width: 100%;
      padding-left: var(--spacing-8);
      padding-right: var(--spacing-8);
    }

    .holder {
      margin-top: var(--spacing-32);
      background-color: var(--main-dark-light-2);
      border-radius: 10px;
      padding: var(--spacing-30);
      height: 296px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .rating {
        margin-bottom: var(--spacing-16);
      }

      .info {
        margin-top: var(--spacing-24);
        display: flex;
        align-items: center;

        img {
          border-radius: 50%;
          margin-right: var(--spacing-16);
        }

        div {
          color: rgba(255, 255, 255, 0.48);

          &.name {
            color: var(--main-light-shade-3);
            display: flex;
            justify-content: center;
            align-items: center;

            svg {
              width: 20px;
              height: 20px;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .testimonials-page {
    .hero-section {
      .head {
        p {
          padding: 0 66px;
        }
      }

      .info {
        margin-top: 167px;
      }
    }

    .story {
      .btn-primary {
        width: 225px;
      }
    }

    .testimonials .tes-card .side-b .ttl {
      font-size: 28px;
    }

    .graduates-work {
      .logos {
        .logo {
          padding: var(--spacing-28) 30px;
          margin: var(--spacing-12);
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .testimonials-page {
    .hero-section .head {
      p {
        padding: 0 66px;
      }
    }

    .testimonials .tes-card .side-b .ttl {
      font-size: 24px;
    }

    .testimonials {
      .story {
        margin-top: var(--spacing-100);

        .subtitle {
          font-size: 21px;
        }

        .btn-primary {
          width: 100%;
          max-width: 328px;
        }
      }
    }

    .graduates-work {
      border-radius: 0;
      margin-top: var(--spacing-100);
      margin-left: -24px;
      margin-right: -24px;
      padding: var(--spacing-80) var(--spacing-24);

      .h2 {
        padding: 0;
      }

      .logos {
        .logo {
          padding: var(--spacing-24) var(--spacing-32);
          margin: 10px 8px;

          svg {
            width: 85px;
          }
        }
      }
    }

    .our-students {
      padding: var(--spacing-80) 0;
      margin-top: 0;

      h2 {
        padding: 0;
      }

      .reviews {
        flex-direction: column;
      }

      .holder {
        height: auto;
        margin-top: var(--spacing-16);
        padding: var(--spacing-32);
      }
    }

    .pre-footer {
      .subtitle {
        width: 100%;
      }
    }
  }
}

@include bp($bp-phone) {
  .testimonials-page {
    .hero-section {
      background-size: cover;
      min-height: 100vh;
      padding: var(--spacing-70) 0;

      > div {
        padding-top: var(--spacing-80);

        :first-child {
          margin: 0;

          .head {
            padding: 0;

            h1 {
              font-size: 36px;
            }

            p {
              padding: 0;
            }
          }
        }
      }

      .info {
        margin-top: 125px;
        column-gap: 0;
        justify-content: space-between;

        .block {
          max-width: 33.33333%;

          .ttl {
            font-size: 36px;
            line-height: 100%;
          }
        }
      }
    }

    .testimonials {
      padding-top: var(--spacing-100);

      .cards {
        margin-top: var(--spacing-26);
      }
    }

    .graduates-work {
      padding: var(--spacing-80) var(--spacing-16);
    }
  }
}
</style>
